import { useEffect, useMemo, useState } from "react";

import { AlertTriangle, Loader2 } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useRecordOverconsumption } from "~/hooks/production";
import {
  getStockAt,
  useOilTypeStockByPlant,
  useRawMaterialStockByPlant,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { UNIT_LABELS } from "~/types/purchasing";
import { formatCurrency } from "~/utils/helpers";
import { costPerKgClp } from "~/utils/oilConversions";

import type { ProductionBatch } from "~/types/production";

interface OverconsumptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batch: ProductionBatch;
  onSuccess: () => void;
}

// One selectable ingredient of the lote (deduped), with the data needed to price it.
interface IngredientOption {
  key: string;
  kind: "oil" | "raw_material";
  id: string;
  name: string;
  unit: string; // entry unit: "kg" for oils, the material's own unit
  unitCostClp: number | null; // per-kg for oils, per-unit for materials
  density: number | null;
}

export function OverconsumptionDialog({
  open,
  onOpenChange,
  batch,
  onSuccess,
}: OverconsumptionDialogProps) {
  const { toast } = useToast();
  const record = useRecordOverconsumption();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawStockByPlant } = useRawMaterialStockByPlant();

  const [ingredientKey, setIngredientKey] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset when reopened.
  useEffect(() => {
    if (open) {
      setIngredientKey("");
      setQuantity("");
      setNotes("");
      setConfirmOpen(false);
    }
  }, [open]);

  // Distinct real ingredients of the lote (dedup by ingredient id; skip the extra
  // sobreconsumo rows which point at the same ingredient).
  const options = useMemo<IngredientOption[]>(() => {
    const seen = new Set<string>();
    const list: IngredientOption[] = [];
    for (const ing of batch.ingredients ?? []) {
      if (ing.oil_type_id && ing.oil_type) {
        if (seen.has(`oil-${ing.oil_type_id}`)) continue;
        seen.add(`oil-${ing.oil_type_id}`);
        const oil = ing.oil_type;
        list.push({
          key: `oil-${ing.oil_type_id}`,
          kind: "oil",
          id: ing.oil_type_id,
          name: oil.code ? `${oil.code} - ${oil.name}` : oil.name,
          unit: "kg",
          unitCostClp:
            oil.average_cost_clp != null && oil.density_kg_per_liter
              ? costPerKgClp(oil.average_cost_clp, oil.density_kg_per_liter)
              : null,
          density: oil.density_kg_per_liter ?? null,
        });
      } else if (ing.raw_material_id && ing.raw_material) {
        if (seen.has(`rm-${ing.raw_material_id}`)) continue;
        seen.add(`rm-${ing.raw_material_id}`);
        const rm = ing.raw_material;
        list.push({
          key: `rm-${ing.raw_material_id}`,
          kind: "raw_material",
          id: ing.raw_material_id,
          name: rm.code ? `${rm.code} - ${rm.name}` : rm.name,
          unit: rm.unit,
          unitCostClp: rm.average_cost_clp ?? null,
          density: null,
        });
      }
    }
    return list;
  }, [batch.ingredients]);

  const selected = options.find((o) => o.key === ingredientKey) ?? null;
  const qtyNum = parseFloat(quantity) || 0;
  const plant = batch.plant ?? "";

  const unitLabel = selected
    ? selected.kind === "oil"
      ? "kg"
      : (UNIT_LABELS[selected.unit] ?? selected.unit)
    : "";

  const estimatedCost =
    selected?.unitCostClp != null ? selected.unitCostClp * qtyNum : null;

  // Available stock at the lote's plant, in the entry unit (kg for oils).
  const availableStock = useMemo(() => {
    if (!selected || !plant) return null;
    if (selected.kind === "oil") {
      const liters = getStockAt(oilStockByPlant, selected.id, plant);
      return selected.density != null ? liters * selected.density : liters;
    }
    return getStockAt(rawStockByPlant, selected.id, plant);
  }, [selected, plant, oilStockByPlant, rawStockByPlant]);

  const insufficient =
    availableStock != null && qtyNum > 0 && qtyNum > availableStock;

  const canSubmit = !!selected && qtyNum > 0 && !!plant && !record.isPending;

  const handleConfirm = () => {
    if (!selected || !plant) return;
    record.mutate(
      {
        batch_id: batch.id,
        ingredient_kind: selected.kind,
        oil_type_id: selected.kind === "oil" ? selected.id : null,
        raw_material_id: selected.kind === "raw_material" ? selected.id : null,
        extra_quantity: qtyNum,
        unit: selected.unit,
        plant,
        notes: notes.trim() || null,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Sobreconsumo registrado",
            description: `Se descontaron ${qtyNum} ${unitLabel} · $${formatCurrency(
              res.total_cost_clp
            )} sumado al costo del lote.`,
          });
          setConfirmOpen(false);
          onOpenChange(false);
          onSuccess();
        },
        onError: (error) => {
          setConfirmOpen(false);
          toast({
            title: "No se pudo registrar",
            description: (error as Error).message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Registrar sobreconsumo</DialogTitle>
            <DialogDescription>
              Consumo adicional al de la receta para el lote {batch.batch_number}.
              El costo se suma al costo del producto terminado.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Ingrediente</Label>
              <Select value={ingredientKey} onValueChange={setIngredientKey}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Selecciona un ingrediente del lote" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((o) => (
                    <SelectItem key={o.key} value={o.key}>
                      <span className="flex items-center gap-2">
                        <Badge
                          variant={o.kind === "oil" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {o.kind === "oil" ? "Aceite" : "Material"}
                        </Badge>
                        {o.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {options.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  El lote no tiene ingredientes registrados.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="oc-qty">Cantidad adicional consumida</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="oc-qty"
                  type="number"
                  step="any"
                  min="0"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                  className="h-9"
                />
                <span className="w-12 text-sm text-muted-foreground">
                  {unitLabel || "—"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Planta del lote: {plant || "sin planta"}
              </p>
            </div>

            {selected && (
              <div className="rounded-lg border bg-muted/50 p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Costo estimado</span>
                  <span className="font-semibold tabular-nums">
                    {estimatedCost != null ? (
                      `$${formatCurrency(estimatedCost)}`
                    ) : (
                      <span className="text-muted-foreground">
                        Sin costo en ficha
                      </span>
                    )}
                  </span>
                </div>
                {availableStock != null && (
                  <div className="mt-1 flex justify-between">
                    <span className="text-muted-foreground">
                      Stock disponible ({plant})
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {availableStock.toFixed(2)} {unitLabel}
                    </span>
                  </div>
                )}
              </div>
            )}

            {insufficient && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  El consumo supera el stock disponible en {plant}. Puedes
                  continuar, pero el stock quedará negativo.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="oc-notes">Notas (opcional)</Label>
              <Textarea
                id="oc-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Motivo del sobreconsumo…"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar sobreconsumo</AlertDialogTitle>
            <AlertDialogDescription>
              Se descontarán {qtyNum} {unitLabel} de {selected?.name}
              {estimatedCost != null
                ? ` y se sumarán $${formatCurrency(estimatedCost)} al costo del lote ${batch.batch_number}.`
                : ` al lote ${batch.batch_number}.`}{" "}
              ¿Confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={record.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              disabled={record.isPending}
            >
              {record.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
